/**
 * Iter51 Etapa 8 — Admin Field Parity Report dashboard.
 *
 * Visualizes the 121-field × 5-layer coverage matrix from
 * `/api/admin/field-parity-report`. Layout:
 *   - Hero card with overall % + 5 layer pills (DB / API GET / API WRITE / VIEW / EDIT)
 *   - Coverage per category table
 *   - Full matrix table with filter (search by field_key/label) + state filter (all / gaps only / sensitive)
 *
 * Admin-only.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import {
  RefreshCw, Database, Network, FileEdit, Eye, PencilLine, ShieldAlert,
  Loader2, CheckCircle2, XCircle, BarChart3,
} from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LAYERS = [
  { key: "db", label: "DB", icon: Database, color: "indigo" },
  { key: "api_get", label: "API GET", icon: Network, color: "cyan" },
  { key: "api_write", label: "API WRITE", icon: FileEdit, color: "blue" },
  { key: "view", label: "VIEW", icon: Eye, color: "emerald" },
  { key: "edit", label: "EDIT", icon: PencilLine, color: "amber" },
];

const Tick = ({ ok }) => ok
  ? <CheckCircle2 className="h-4 w-4 text-emerald-600 inline" />
  : <XCircle className="h-4 w-4 text-red-500 inline" />;

const StatPill = ({ icon: Icon, label, value, total, color }) => {
  const pct = total ? Math.round((100 * value) / total) : 0;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 flex items-start gap-3" data-testid={`layer-pill-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className={`h-9 w-9 rounded-md bg-${color}-50 text-${color}-700 flex items-center justify-center flex-shrink-0`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">{label}</p>
        <div className="mt-0.5 flex items-baseline gap-1.5">
          <span className="text-xl font-semibold text-slate-900">{value}</span>
          <span className="text-sm text-slate-500">/ {total}</span>
          <span className={`text-xs ml-auto font-semibold ${pct === 100 ? "text-emerald-600" : pct >= 90 ? "text-amber-600" : "text-red-600"}`}>{pct}%</span>
        </div>
        <div className="mt-2 h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full ${pct === 100 ? "bg-emerald-500" : pct >= 90 ? "bg-amber-500" : "bg-red-500"}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
    </div>
  );
};

const FieldParityReport = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [scope, setScope] = useState("all"); // "all" | "gaps" | "sensitive" | "full"
  const [category, setCategory] = useState("all");

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API_URL}/admin/field-parity-report`, { timeout: 60000 });
      setData(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      setError(detail);
      toast.error(`Eroare la încărcare raport: ${detail}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReport(); }, []);

  const matrix = data?.matrix || [];
  const summary = data?.summary || {};
  const categories = Object.keys(summary.by_category || {}).sort();

  const filtered = useMemo(() => {
    let rows = matrix;
    if (scope === "gaps") {
      rows = rows.filter((r) => !LAYERS.every((L) => r[L.key]));
    } else if (scope === "full") {
      rows = rows.filter((r) => LAYERS.every((L) => r[L.key]));
    } else if (scope === "sensitive") {
      rows = rows.filter((r) => r.sensitive);
    }
    if (category !== "all") {
      rows = rows.filter((r) => r.category === category);
    }
    const q = query.trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) =>
        r.field_key.toLowerCase().includes(q) ||
        (r.label || "").toLowerCase().includes(q) ||
        (r.tech_name || "").toLowerCase().includes(q)
      );
    }
    return rows;
  }, [matrix, scope, category, query]);

  return (
    <div className="space-y-5 animate-fade-in" data-testid="field-parity-page">
      {/* ===== Header ===== */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-blue-600" />
            Raport Acoperire Câmpuri (Field Parity)
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Matricea 121 câmpuri × 5 layere (DB · API GET · API WRITE · VIEW · EDIT). Auditul rulează în timp real, ~5-10s.
            {data?.sample?.sale_id && (
              <span className="block mt-0.5 text-xs text-slate-400" data-testid="parity-sample-info">
                Eșantion API GET: vânzarea <code>{data.sample.sale_id.slice(0, 8)}</code> (HTTP {data.sample.sale_http})
                {data.sample.client_id && <> · client <code>{data.sample.client_id.slice(0, 8)}</code> (HTTP {data.sample.client_http})</>}
              </span>
            )}
          </p>
        </div>
        <Button onClick={fetchReport} disabled={loading} variant="outline" size="sm" data-testid="refresh-report-btn">
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
          {loading ? "Se rulează auditul..." : "Reîncarcă"}
        </Button>
      </div>

      {/* ===== Loading / Error ===== */}
      {loading && !data && (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-12 flex flex-col items-center justify-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            <p className="text-sm text-slate-500">Se rulează auditul... (5-10s)</p>
          </CardContent>
        </Card>
      )}
      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <p className="text-sm text-red-800">⚠ {error}</p>
          </CardContent>
        </Card>
      )}

      {data && (
        <>
          {/* ===== Hero summary card ===== */}
          <Card className="border-slate-200 shadow-sm" data-testid="hero-summary">
            <CardContent className="p-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Total câmpuri în catalog</p>
                  <p className="mt-1 text-3xl font-semibold text-slate-900">{summary.total}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Acoperite complet (toate 5 layere)</p>
                  <div className="mt-1 flex items-baseline gap-2">
                    <p className="text-3xl font-semibold text-emerald-600" data-testid="fully-covered-count">{summary.fully_covered}</p>
                    <p className="text-lg text-slate-400">/ {summary.total}</p>
                    <Badge className={`ml-2 ${summary.fully_covered === summary.total ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-amber-100 text-amber-800 border-amber-200"} border text-xs`}>
                      {summary.total ? Math.round(100 * summary.fully_covered / summary.total) : 0}%
                    </Badge>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                {LAYERS.map((L) => (
                  <StatPill
                    key={L.key}
                    icon={L.icon}
                    label={L.label}
                    value={(summary.by_layer || {})[L.key] || 0}
                    total={summary.total || 0}
                    color={L.color}
                  />
                ))}
              </div>
            </CardContent>
          </Card>

          {/* ===== Per-category breakdown ===== */}
          <Card className="border-slate-200 shadow-sm" data-testid="category-table">
            <CardContent className="p-5">
              <h3 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-3">Acoperire per categorie</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                {categories.map((cat) => {
                  const s = summary.by_category[cat];
                  const pct = s.total ? Math.round(100 * s.full / s.total) : 0;
                  return (
                    <div key={cat} className="rounded-md border border-slate-200 px-3 py-2.5 flex items-center justify-between hover:bg-slate-50 cursor-pointer" onClick={() => setCategory(cat)} data-testid={`cat-${cat.toLowerCase().replace(/\s+/g, '-')}`}>
                      <div>
                        <p className="text-sm font-medium text-slate-900">{cat}</p>
                        <p className="text-xs text-slate-500">{s.full} / {s.total}</p>
                      </div>
                      <Badge className={`${pct === 100 ? "bg-emerald-100 text-emerald-700" : pct >= 80 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"} border text-xs`}>
                        {pct}%
                      </Badge>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* ===== Matrix table with filters ===== */}
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                <h3 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Matrice câmpuri ({filtered.length})</h3>
                <div className="flex items-center gap-2 flex-wrap">
                  <Input
                    placeholder="Caută după field_key sau label..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="h-8 w-64 text-sm"
                    data-testid="search-input"
                  />
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger className="h-8 w-44 text-sm" data-testid="category-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toate categoriile</SelectItem>
                      {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={scope} onValueChange={setScope}>
                    <SelectTrigger className="h-8 w-40 text-sm" data-testid="scope-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toate câmpurile</SelectItem>
                      <SelectItem value="gaps">Doar cu gap-uri</SelectItem>
                      <SelectItem value="full">Doar 5/5 complete</SelectItem>
                      <SelectItem value="sensitive">Doar sensibile</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500">
                      <th className="py-2 px-2 text-left font-medium">Categorie</th>
                      <th className="py-2 px-2 text-left font-medium">Câmp</th>
                      <th className="py-2 px-2 text-left font-medium">Label</th>
                      <th className="py-2 px-2 text-left font-medium">Tech</th>
                      <th className="py-2 px-2 text-center font-medium">Sens.</th>
                      {LAYERS.map((L) => (
                        <th key={L.key} className="py-2 px-2 text-center font-medium">{L.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 && (
                      <tr><td colSpan={9} className="py-6 text-center text-slate-400 italic">Niciun câmp în filtrul curent</td></tr>
                    )}
                    {filtered.map((r) => {
                      const fully = LAYERS.every((L) => r[L.key]);
                      return (
                        <tr key={r.field_key} className={`border-b border-slate-100 hover:bg-slate-50 ${fully ? "" : "bg-amber-50/30"}`} data-testid={`row-${r.field_key}`}>
                          <td className="py-1.5 px-2 text-xs text-slate-500">{r.category}</td>
                          <td className="py-1.5 px-2 font-mono text-xs text-slate-700">{r.field_key}</td>
                          <td className="py-1.5 px-2 text-slate-900">
                            {r.label || "—"}
                            {r.note && (
                              <p className="text-[11px] text-slate-500 mt-0.5 max-w-md" data-testid={`note-${r.field_key}`}>{r.note}</p>
                            )}
                          </td>
                          <td className="py-1.5 px-2 font-mono text-[11px] text-slate-400">{r.tech_name}</td>
                          <td className="py-1.5 px-2 text-center">
                            {r.sensitive && <ShieldAlert className="h-3.5 w-3.5 text-amber-600 inline" />}
                          </td>
                          {LAYERS.map((L) => (
                            <td key={L.key} className="py-1.5 px-2 text-center" title={L.key === "edit" || L.key === "view" ? (r.note || undefined) : undefined}><Tick ok={r[L.key]} /></td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default FieldParityReport;
